import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartBarIcon,
  BanknotesIcon,
  CheckBadgeIcon,
  ArrowTrendingUpIcon,
  ShieldExclamationIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/20/solid';
import DisclaimerFooter from '../DisclaimerFooter';

const QuantFactorsPage = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const quantFactors = [
    {
      id: 'enhanced-value',
      name: 'Enhanced Value',
      description: 'Incorporates forward-looking earnings and sector adjustments for a dynamic measure of intrinsic value.',
      icon: BanknotesIcon,
      details: `Traditional value metrics like P/E ratios use historical data and don't account for sector differences. Our Enhanced Value factor creates a more sophisticated measure of whether a stock is truly undervalued.

      Our enhancements include:
      • Forward-Looking Earnings: Using our proprietary earnings expectations instead of historical data
      • Sector Normalization: Comparing companies to peers rather than the entire market
      • Quality Adjustment: Accounting for earnings quality when calculating valuation metrics
      • Multiple Valuation Approaches: Combining price-to-earnings, price-to-book, and cash flow metrics

      This creates a more accurate picture of intrinsic value, helping identify genuinely undervalued companies rather than value traps.`
    },
    {
      id: 'enhanced-quality',
      name: 'Enhanced Quality',
      description: 'Focuses on sustainable earnings, cash flow consistency, and balance sheet strength, moving beyond simple earnings quality measures.',
      icon: CheckBadgeIcon,
      details: `Quality companies generate consistent, sustainable returns over time. Our Enhanced Quality factor identifies companies with strong fundamentals that can weather market volatility.

      We evaluate:
      • Earnings Sustainability: Separating one-time items from recurring business performance
      • Cash Flow Consistency: Analyzing whether earnings translate to actual cash generation
      • Balance Sheet Strength: Evaluating debt levels, working capital management, and financial flexibility
      • Accruals Quality: Understanding whether earnings growth is supported by underlying business performance

      This helps identify companies with durable business models that can provide stable returns through different market conditions.`
    },
    {
      id: 'enhanced-momentum',
      name: 'Enhanced Momentum',
      description: 'Uses peer benchmarking and economic linkages to capture robust, company-specific momentum while controlling for market-wide effects.',
      icon: ArrowTrendingUpIcon,
      details: `Traditional momentum strategies often capture broad market movements rather than company-specific performance. Our Enhanced Momentum factor isolates genuine company outperformance.

      Our methodology includes:
      • Peer-Relative Performance: Comparing companies to economically linked firms rather than broad indices
      • Cross-Stock Effects: Analyzing how performance spreads through supply chains and industry relationships
      • Time-Varying Momentum: Adjusting momentum calculations based on market conditions
      • Risk-Adjusted Returns: Focusing on momentum that's not explained by other risk factors

      This creates a cleaner momentum signal that identifies companies with genuine business momentum rather than just riding market trends.`
    },
    {
      id: 'enhanced-volatility',
      name: 'Enhanced Volatility',
      description: 'Isolates the component of volatility predictive of positive returns, controlling for unintended factor exposures.',
      icon: ShieldExclamationIcon,
      details: `Not all volatility is bad - some reflects uncertainty that creates opportunities. Our Enhanced Volatility factor identifies the type of volatility that historically leads to positive returns.

      We distinguish between:
      • Fundamental Volatility: Price movements driven by changing business fundamentals
      • Technical Volatility: Price movements driven by market structure and sentiment
      • Sector-Specific Patterns: How volatility affects different types of businesses
      • Mean Reversion Opportunities: Identifying when volatility creates temporary mispricings

      This helps us find companies where current volatility represents opportunity rather than risk, allowing us to capture returns from temporary market inefficiencies.`
    }
  ];

  return (
    <div className="min-h-screen bg-bg-100">
      <div className="px-6 py-8">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <div className="flex items-center text-sm text-text-300">
            <Link to="/learn" className="hover:text-primary-100 transition-colors">
              Learn
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" />
            <span className="text-text-100">Quant Factors</span>
          </div>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-100 mb-4 flex items-center gap-3">
            <ChartBarIcon className="h-8 w-8 text-primary-100" />
            Enhanced Quantitative Factors
          </h1>
          <p className="text-lg text-text-200">
            Advanced factor models that improve upon traditional quantitative approaches.
          </p>
        </div>

        {/* Introduction */}
        <div className="mb-8 bg-bg-200 rounded-lg p-6 border border-bg-300">
          <h2 className="text-xl font-semibold text-text-100 mb-4">Beyond Traditional Factors</h2>
          <p className="text-text-200 leading-relaxed">
            While traditional quantitative factors like value, quality, and momentum have proven effective over time, they often suffer from 
            limitations such as using stale data, ignoring sector differences, or capturing unintended market effects. Our enhanced factors 
            address these shortcomings through sophisticated adjustments and forward-looking inputs.
          </p>
        </div>

        {/* Enhanced Quantitative Factors */}
        <div className="space-y-4">
          {quantFactors.map((factor) => (
            <div key={factor.id} className="bg-bg-200 rounded-lg border border-bg-300">
              <button
                onClick={() => toggleSection(factor.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-bg-300 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <factor.icon className="h-5 w-5 text-primary-100" />
                  <div>
                    <h3 className="font-semibold text-text-100">{factor.name}</h3>
                    <p className="text-sm text-text-200 mt-1">{factor.description}</p>
                  </div>
                </div>
                {expandedSections[factor.id] ? (
                  <ChevronDownIcon className="h-5 w-5 text-text-200" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-text-200" />
                )}
              </button>
              {expandedSections[factor.id] && (
                <div className="px-6 pb-4">
                  <div className="text-text-200 leading-relaxed whitespace-pre-line">
                    {factor.details}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
};

export default QuantFactorsPage; 